import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { caissesApi } from '../../services/api';
import type { Caisse } from '../../types';
import { X, Building2, MapPin, FileText, Save, Loader2, Upload, Trash2, ExternalLink } from 'lucide-react';

interface CaisseModalProps {
  caisse: Caisse | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CaisseModal: React.FC<CaisseModalProps> = ({ caisse, onClose, onSuccess }) => {
  const isEditing = !!caisse;

  const [formData, setFormData] = useState({
    caisse: '',
    address: '',
    npa: '',
    localite: '',
    formulaire_instruction: false,
  });

  // File states
  const [formulairePdfFile, setFormulairePdfFile] = useState<File | null>(null);
  const [deleteFormulairePdf, setDeleteFormulairePdf] = useState(false);

  // File input refs
  const formulairePdfInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (caisse) {
      setFormData({
        caisse: caisse.caisse || '',
        address: caisse.address || '',
        npa: caisse.npa || '',
        localite: caisse.localite || '',
        formulaire_instruction: caisse.formulaire_instruction || false,
      });
      // Reset file states when editing
      setFormulairePdfFile(null);
      setDeleteFormulairePdf(false);
    }
  }, [caisse]);

  const createMutation = useMutation({
    mutationFn: (data: FormData) => caissesApi.create(data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      const axiosErr = error as { response?: { data?: { errors?: Record<string, string>; message?: string } } };
      if (axiosErr.response?.data?.errors) {
        setErrors(axiosErr.response.data.errors);
      } else {
        setErrors({ general: axiosErr.response?.data?.message || 'Une erreur est survenue' });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => caissesApi.update(caisse!.id, data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      const axiosErr = error as { response?: { data?: { errors?: Record<string, string>; message?: string } } };
      if (axiosErr.response?.data?.errors) {
        setErrors(axiosErr.response.data.errors);
      } else {
        setErrors({ general: axiosErr.response?.data?.message || 'Une erreur est survenue' });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    if (!formData.caisse.trim()) {
      setErrors({ caisse: 'Le nom de la caisse est requis' });
      return;
    }

    // Validate PDF is required if boolean is true
    if (formData.formulaire_instruction && !formulairePdfFile && !caisse?.formulaire_instruction_pdf) {
      setErrors({ formulaire_instruction_pdf: 'Un fichier PDF est requis pour le formulaire d\'instruction' });
      return;
    }

    // Build FormData
    const data = new FormData();
    data.append('caisse', formData.caisse.trim());
    if (formData.address.trim()) data.append('address', formData.address.trim());
    if (formData.npa.trim()) data.append('npa', formData.npa.trim());
    if (formData.localite.trim()) data.append('localite', formData.localite.trim());
    data.append('formulaire_instruction', formData.formulaire_instruction ? '1' : '0');

    // Add files if selected
    if (formulairePdfFile) {
      data.append('formulaire_instruction_pdf', formulairePdfFile);
    }

    // Add delete flags
    if (deleteFormulairePdf) {
      data.append('delete_formulaire_instruction_pdf', '1');
    }

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: React.Dispatch<React.SetStateAction<File | null>>,
    setDelete: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setDelete(false); // Cancel any pending delete
    }
  };

  const handleRemoveFile = (
    setFile: React.Dispatch<React.SetStateAction<File | null>>,
    setDelete: React.Dispatch<React.SetStateAction<boolean>>,
    hasExisting: boolean
  ) => {
    setFile(null);
    if (hasExisting) {
      setDelete(true);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Get file URL for viewing existing files (stored in laravel-api storage)
  const getFileUrl = (path: string | undefined) => {
    if (!path) return null;
    // If it's already a full URL, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Use laravel-api files endpoint
    const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8001/api/v1';
    const baseUrl = apiUrl.replace('/api/v1', '');
    return `${baseUrl}/api/files?path=${encodeURIComponent(path)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Modifier la caisse' : 'Nouvelle caisse'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {errors.general}
              </div>
            )}

            {/* Informations de base */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Informations de base
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la caisse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.caisse}
                    onChange={(e) => setFormData({ ...formData, caisse: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none ${
                      errors.caisse ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Caisse de pension XYZ"
                  />
                  {errors.caisse && (
                    <p className="mt-1 text-sm text-red-500">{errors.caisse}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                    placeholder="Adresse de la caisse..."
                  />
                </div>
              </div>
            </div>

            {/* Localisation */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Localisation
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NPA
                  </label>
                  <input
                    type="text"
                    value={formData.npa}
                    onChange={(e) => setFormData({ ...formData, npa: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Ex: 1000"
                    maxLength={20}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Localité
                  </label>
                  <input
                    type="text"
                    value={formData.localite}
                    onChange={(e) => setFormData({ ...formData, localite: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Ex: Lausanne"
                  />
                </div>
              </div>
            </div>

            {/* Formulaires et instructions */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Formulaires et instructions
              </h3>
              <div className="space-y-6">
                {/* Formulaire d'instruction */}
                <div className="p-4 border border-gray-200 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Formulaire d'instruction requis ?
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, formulaire_instruction: false })}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          !formData.formulaire_instruction
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Non
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, formulaire_instruction: true })}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          formData.formulaire_instruction
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Oui
                      </button>
                    </div>
                  </div>

                  {formData.formulaire_instruction && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        PDF du formulaire <span className="text-red-500">*</span>
                      </label>

                      {/* Show existing file or newly selected file */}
                      {(caisse?.formulaire_instruction_pdf && !deleteFormulairePdf && !formulairePdfFile) ? (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <FileText className="w-5 h-5 text-red-500" />
                          <span className="text-sm text-gray-700 flex-1 truncate">
                            {caisse.formulaire_instruction_pdf.split('/').pop()}
                          </span>
                          <a
                            href={getFileUrl(caisse.formulaire_instruction_pdf) || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded"
                            title="Voir le fichier"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(setFormulairePdfFile, setDeleteFormulairePdf, true)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : formulairePdfFile ? (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-gray-700 flex-1 truncate">
                            {formulairePdfFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setFormulairePdfFile(null);
                              if (formulairePdfInputRef.current) formulairePdfInputRef.current.value = '';
                            }}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => formulairePdfInputRef.current?.click()}
                          className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors"
                        >
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">Cliquez pour télécharger un PDF</span>
                          <span className="text-xs text-gray-400 mt-1">PDF uniquement, max 10MB</span>
                        </div>
                      )}

                      <input
                        ref={formulairePdfInputRef}
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => handleFileChange(e, setFormulairePdfFile, setDeleteFormulairePdf)}
                      />

                      {errors.formulaire_instruction_pdf && (
                        <p className="text-sm text-red-500">{errors.formulaire_instruction_pdf}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Enregistrer' : 'Créer'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaisseModal;
